/*
 * BB4x4 garment customiser (tees and hoodies)
 * Live preview of a printed design on the shirt, plus the design picker.
 * Each colour is its own Shopify product with Size as the only variant, so
 * stock is held per colour + size; the chosen design travels to the cart as
 * line item properties ("Design Art ID" for the press, plus its name) and
 * never becomes inventory.
 */
(function () {
  'use strict';

  // Per garment: the colours (the product's blank colour; slug names the photo
  // files <folder>/<slug>[-back][-shade].webp made by scripts/process_tees.py;
  // hex is the measured fabric colour behind the picker thumbnails), and the
  // print rule. Photos are aligned to a 100 x 100 frame in which one unit is
  // cmPerUnit on an adult M; printTop is the top of the print in that frame.
  var GARMENTS = {
    tee: {
      label: 'T-shirt',
      folder: 'tees',
      colours: {
        'White':      { slug: 'white',      hex: '#E7E7EA' },
        'Black':      { slug: 'black',      hex: '#1C1C1B' },
        'Grey':       { slug: 'grey',       hex: '#9E9EA0' },
        'Light Blue': { slug: 'light-blue', hex: '#A8CFF3' },
        'Light Pink': { slug: 'light-pink', hex: '#EAC8CB' },
        'Hot Pink':   { slug: 'hot-pink',   hex: '#F52F7A' }
      },
      frameW: 100,                     // photos are 1600 x 1600
      // Back, upper middle, 8 cm below the back collar seam (at ~12).
      cmPerUnit: 0.908,
      printTop: 21,
      sizes: {                         // cm; made ~50% bigger at Wade's request
        art:  { maxW: 40, maxH: 48 },  // full illustrations
        logo: { maxW: 30, maxH: 30 }   // logos and text marks
      },
      // which designs this garment offers, and on which colours
      offers: function (d) { return true; },
      colourOk: function (d, colour) { return !d.colours || d.colours.indexOf(colour) >= 0; }
    },
    hoodie: {
      label: 'Hoodie',
      folder: 'hoodies',
      // Photos are 1200 x 1600 (75 x 100 units), figures framed slightly
      // differently, so each colour carries its own scale and print top:
      // ~4 cm below where the hood meets the back, scaled off the back width
      // between the shoulder seams (~60 cm on an oversized M).
      colours: {
        'Black':    { slug: 'black',    hex: '#161617', cmPerUnit: 0.94, printTop: 30.3 },
        'Grey':     { slug: 'grey',     hex: '#A5A7AB', cmPerUnit: 1.13, printTop: 33.5 },
        'Hot Pink': { slug: 'hot-pink', hex: '#E4286F', cmPerUnit: 1.09, printTop: 33.7 }
      },
      frameW: 75,
      cmPerUnit: 1.05,
      printTop: 33,
      sizes: {
        art:  { maxW: 34, maxH: 40 },
        logo: { maxW: 32, maxH: 32 }
      },
      // hoodies carry BB1-BB7 only, each on the colours in the SOH mapping
      offers: function (d) { return !!d.hoodieColours; },
      colourOk: function (d, colour) { return !!d.hoodieColours && d.hoodieColours.indexOf(colour) >= 0; }
    }
  };

  var uid = 0;

  // The shirt photo, then two print slots for crossfading designs. Each slot
  // is the design plus the shirt's shading map, multiplied over the design
  // and masked to its shape, so folds and fabric texture run through the print.
  function teeSVG(id, W) {
    W = W || 100;
    function slot(k) {
      return '<g class="bbt-print bbt-print-' + k + '">' +
        '<image class="bbt-art" preserveAspectRatio="none"/>' +
        '<image class="bbt-shade" x="0" y="0" width="' + W + '" height="100" preserveAspectRatio="none" mask="url(#' + id + '-m' + k + ')"/>' +
        '</g>';
    }
    return '' +
      '<svg class="bbt-svg" viewBox="0 0 ' + W + ' 100" role="img" aria-label="Garment preview">' +
      '<defs>' +
        '<mask id="' + id + '-ma" maskUnits="userSpaceOnUse" x="0" y="0" width="' + W + '" height="100" style="mask-type:alpha"><image class="bbt-mask" preserveAspectRatio="none"/></mask>' +
        '<mask id="' + id + '-mb" maskUnits="userSpaceOnUse" x="0" y="0" width="' + W + '" height="100" style="mask-type:alpha"><image class="bbt-mask" preserveAspectRatio="none"/></mask>' +
      '</defs>' +
      '<image class="bbt-photo bbt-photo-a is-on" x="0" y="0" width="' + W + '" height="100"/>' +
      '<image class="bbt-photo bbt-photo-b" x="0" y="0" width="' + W + '" height="100"/>' +
      slot('a') + slot('b') +
      '</svg>';
  }

  function esc(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function printBox(design, g, colour) {
    g = g || GARMENTS.tee;
    var c = (colour && g.colours[colour]) || {};
    var cm = c.cmPerUnit || g.cmPerUnit, top = c.printTop || g.printTop, W = g.frameW || 100;
    var lim = g.sizes[design.kind] || g.sizes.art;
    var w = Math.min(lim.maxW, lim.maxH * design.aspect) / cm;
    var h = w / design.aspect;
    return { x: W / 2 - w / 2, y: top, w: w, h: h };
  }

  function setBox(el, box) {
    el.setAttribute('x', box.x); el.setAttribute('y', box.y);
    el.setAttribute('width', box.w); el.setAttribute('height', box.h);
  }

  // A cloned SVG must not share mask ids with the live one, or its masks
  // would follow the live preview as the customer keeps choosing.
  var cloneCount = 0;
  function uniquify(svg) {
    var suffix = '-c' + (++cloneCount);
    svg.querySelectorAll('[id]').forEach(function (n) { n.id = n.id + suffix; });
    svg.querySelectorAll('[mask]').forEach(function (n) {
      n.setAttribute('mask', n.getAttribute('mask').replace(/\)$/, suffix + ')'));
    });
    return svg;
  }

  function Customiser(root, opts) {
    this.root = root;
    this.g = GARMENTS[opts.garment || 'tee'];
    var g = this.g;
    this.designs = opts.designs.filter(function (d) { return g.offers(d); });
    this.byCode = {};
    for (var i = 0; i < this.designs.length; i++) this.byCode[this.designs[i].code] = this.designs[i];
    this.assetUrl = opts.assetUrl || function (code, kind) { return 'designs/' + code + (kind === 'thumb' ? '-thumb.webp' : '.webp'); };
    // photos: tees/<slug>[-back][-shade].webp
    this.teeUrl = opts.teeUrl || function (slug, kind, view) {
      return g.folder + '/' + slug + (view === 'back' ? '-back' : '') + (kind === 'shade' ? '-shade' : '') + '.webp';
    };
    this.view = 'back';
    this.formId = opts.formId || null;
    this.colourLinks = opts.colourLinks || null;   // { colour: product url } for the swatch row
    this.colour = null;
    this.design = null;
    this.tag = 'All';
    this.query = '';
    this.front = 'a';
    this.photoFront = 'a';
    this.id = 'bbt' + (++uid);
    this.render();
    this.setColour(opts.colour || Object.keys(this.g.colours)[0]);
    this.renderColours();
    if (opts.design && this.byCode[opts.design]) this.setDesign(opts.design, true);
    this.preloadColours();
  }

  // Fetch the other shirt photos once the page is idle so switching colour is instant.
  Customiser.prototype.preloadColours = function () {
    var self = this;
    var go = function () {
      Object.keys(self.g.colours).forEach(function (n) {
        var slug = self.g.colours[n].slug;
        new Image().src = self.teeUrl(slug, 'photo', 'back');
        new Image().src = self.teeUrl(slug, 'shade', 'back');
        new Image().src = self.teeUrl(slug, 'photo', 'front');
      });
    };
    if ('requestIdleCallback' in window) requestIdleCallback(go, { timeout: 3000 });
    else setTimeout(go, 1500);
  };

  Customiser.prototype.render = function () {
    var root = this.root;
    var stage = root.querySelector('[data-bbt-stage]');
    var picker = root.querySelector('[data-bbt-picker]');
    var self = this;

    stage.classList.add('bbt-stage');
    stage.style.aspectRatio = (this.g.frameW || 100) + ' / 100';
    stage.innerHTML = teeSVG(this.id, this.g.frameW) +
      '<div class="bbt-views" role="group" aria-label="Shirt view">' +
        '<button type="button" data-view="back" aria-pressed="true">Back</button>' +
        '<button type="button" data-view="front" aria-pressed="false">Front</button>' +
      '</div>' +
      '<button type="button" class="bbt-zoom" aria-pressed="false" disabled>' +
        '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="10.5" cy="10.5" r="6.5"/><path d="M15.5 15.5L21 21M10.5 7.5v6M7.5 10.5h6"/></svg>' +
        '<span>Zoom in on design</span></button>' +
      '<p class="bbt-stage-hint">Pick a design to see it on your shirt</p>';
    this.svg = stage.querySelector('.bbt-svg');
    this.zoomBtn = stage.querySelector('.bbt-zoom');
    this.hint = stage.querySelector('.bbt-stage-hint');
    this.stage = stage;
    this.zoomBtn.addEventListener('click', function () { self.toggleZoom(); });
    stage.querySelector('.bbt-views').addEventListener('click', function (e) {
      var b = e.target.closest('[data-view]');
      if (b) self.setView(b.getAttribute('data-view'));
    });
    this.svg.addEventListener('click', function () { if (self.design && self.view === 'back') self.toggleZoom(); });

    // tags in the order they first appear, with All first
    var tags = ['All'];
    this.designs.forEach(function (d) { d.tags.forEach(function (t) { if (tags.indexOf(t) < 0) tags.push(t); }); });

    var formAttr = this.formId ? ' form="' + esc(this.formId) + '"' : '';
    picker.classList.add('bbt-picker');
    picker.innerHTML =
      '<div class="bbt-picker-head">' +
        '<span class="bbt-label">Design</span>' +
        '<span class="bbt-chosen" aria-live="polite">None selected</span>' +
      '</div>' +
      '<div class="bbt-tools">' +
        '<label class="bbt-search"><span class="bbt-vh">Search designs</span>' +
          '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="10.5" cy="10.5" r="6.5"/><path d="M15.5 15.5L21 21"/></svg>' +
          '<input type="search" placeholder="Search designs, e.g. Navara, rodeo, BB27" autocomplete="off"></label>' +
        '<div class="bbt-tags" role="group" aria-label="Filter designs">' +
          tags.map(function (t) { return '<button type="button" class="bbt-tag" aria-pressed="' + (t === 'All') + '" data-tag="' + esc(t) + '">' + esc(t) + '</button>'; }).join('') +
        '</div>' +
      '</div>' +
      // data-lenis-prevent: Nova's smooth scroll (Lenis) otherwise swallows the wheel here
      '<div class="bbt-grid" role="radiogroup" aria-label="Designs" data-lenis-prevent></div>' +
      '<p class="bbt-empty" hidden>No designs match that search.</p>' +
      '<p class="bbt-error" role="alert" hidden>Choose a design before adding to cart.</p>' +
      '<input type="hidden" name="properties[Design Art ID]" value="" data-bbt-code' + formAttr + '>' +
      '<input type="hidden" name="properties[Design]" value="" data-bbt-input' + formAttr + '>';

    this.input = picker.querySelector('[data-bbt-input]');
    this.codeInput = picker.querySelector('[data-bbt-code]');
    this.chosen = picker.querySelector('.bbt-chosen');
    this.grid = picker.querySelector('.bbt-grid');
    this.empty = picker.querySelector('.bbt-empty');
    this.error = picker.querySelector('.bbt-error');
    this.picker = picker;

    this.grid.innerHTML = this.designs.map(function (d) {
      return '<button type="button" class="bbt-card" role="radio" aria-checked="false" data-code="' + esc(d.code) + '" title="' + esc(d.code + ' ' + d.name) + '">' +
        '<span class="bbt-card-img"><img src="' + esc(self.assetUrl(d.code, 'thumb')) + '" alt="" loading="lazy" decoding="async" width="160" height="160"></span>' +
        '<span class="bbt-card-code">' + esc(d.code) + '</span>' +
        '<span class="bbt-card-name">' + esc(d.name) + '</span>' +
        (d.colours ? '<span class="bbt-card-note">' + esc(d.colourNote || 'Not available in this colour') + '</span>' : '') +
        '</button>';
    }).join('');
    this.cards = Array.prototype.slice.call(this.grid.querySelectorAll('.bbt-card'));

    this.grid.addEventListener('click', function (e) {
      var card = e.target.closest('.bbt-card');
      if (card) self.setDesign(card.getAttribute('data-code'));
    });
    this.grid.addEventListener('keydown', function (e) { self.onGridKey(e); });
    picker.querySelector('.bbt-tags').addEventListener('click', function (e) {
      var b = e.target.closest('.bbt-tag');
      if (!b) return;
      self.tag = b.getAttribute('data-tag');
      picker.querySelectorAll('.bbt-tag').forEach(function (x) { x.setAttribute('aria-pressed', String(x === b)); });
      self.filter();
    });
    var search = picker.querySelector('input[type=search]');
    search.addEventListener('input', function () { self.query = search.value.trim().toLowerCase(); self.filter(); });
    this.updateTabStops();

    // Phones: the big preview scrolls away while browsing the grid, so a
    // small live preview floats in the corner until it is back in view.
    this.mini = document.createElement('button');
    this.mini.type = 'button';
    this.mini.className = 'bbt-mini';
    this.mini.setAttribute('aria-label', 'Back to the shirt preview');
    this.mini.addEventListener('click', function () { stage.scrollIntoView({ behavior: 'smooth', block: 'center' }); });
    root.appendChild(this.mini);
    if ('IntersectionObserver' in window) {
      var seen = { stage: true, picker: false };
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) { seen[en.target === stage ? 'stage' : 'picker'] = en.isIntersecting; });
        self.mini.classList.toggle('is-on', !seen.stage && seen.picker && !!self.design);
      }, { threshold: 0.15 });
      io.observe(stage);
      io.observe(picker);
    }
  };

  Customiser.prototype.cloneSVG = function () {
    var clone = this.svg.cloneNode(true);
    if (this.view === 'front') {
      var back = this.teeUrl(this.g.colours[this.colour].slug, 'photo', 'back');
      clone.querySelector('.bbt-photo.is-on').setAttribute('href', back);
    }
    clone.querySelectorAll('.bbt-print:not(.is-on), .bbt-photo:not(.is-on)').forEach(function (n) { n.remove(); });
    return uniquify(clone);
  };

  Customiser.prototype.renderColours = function () {
    // the swatch row may sit in its own theme block, outside the root
    var box = this.root.querySelector('[data-bbt-colours]') || document.querySelector('[data-bbt-colours]');
    if (!box || !this.colourLinks) return;
    var self = this;
    box.classList.add('bbt-colours');
    box.innerHTML = '<div class="bbt-colours-head"><span class="bbt-label">Colour</span> <span>' + esc(this.colour) + '</span></div>' +
      '<div class="bbt-swatches">' + Object.keys(this.g.colours).filter(function (n) { return self.colourLinks[n]; }).map(function (n) {
        var cur = n === self.colour;
        return '<a class="bbt-swatch" href="' + esc(self.colourLinks[n]) + '" data-href="' + esc(self.colourLinks[n]) + '" title="' + esc(n) + '"' +
          (cur ? ' aria-current="true"' : '') + ' style="--sw:' + self.g.colours[n].hex + '"><span class="bbt-vh">' + esc(n) + '</span></a>';
      }).join('') + '</div>';
    this.swatchLinks = Array.prototype.slice.call(box.querySelectorAll('.bbt-swatch'));
    this.updateColourLinks();
  };

  Customiser.prototype.updateColourLinks = function () {
    var code = this.design ? this.design.code : null;
    (this.swatchLinks || []).forEach(function (a) {
      var u = a.getAttribute('data-href').replace(/([?&])design=[^&]*&?/, '$1').replace(/[?&]$/, '');
      a.setAttribute('href', code ? u + (u.indexOf('?') < 0 ? '?' : '&') + 'design=' + code : u);
    });
  };

  Customiser.prototype.updateMini = function () {
    var clone = this.cloneSVG();
    this.mini.innerHTML = '';
    this.mini.appendChild(clone);
  };

  Customiser.prototype.filter = function () {
    var tag = this.tag, q = this.query, shown = 0;
    var self = this;
    this.cards.forEach(function (card) {
      var d = self.byCode[card.getAttribute('data-code')];
      var hay = (d.code + ' ' + d.name + ' ' + d.tags.join(' ')).toLowerCase();
      var ok = self.available(d) &&
        (tag === 'All' || d.tags.indexOf(tag) >= 0) && (!q || q.split(/\s+/).every(function (w) { return hay.indexOf(w) >= 0; }));
      card.hidden = !ok;
      if (ok) shown++;
    });
    this.empty.hidden = shown > 0;
    this.updateTabStops();
  };

  // roving tabindex so the grid is one tab stop, arrows move between cards
  Customiser.prototype.updateTabStops = function () {
    var visible = this.cards.filter(function (c) { return !c.hidden; });
    var sel = visible.filter(function (c) { return c.getAttribute('aria-checked') === 'true'; })[0] || visible[0];
    this.cards.forEach(function (c) { c.tabIndex = c === sel ? 0 : -1; });
  };

  Customiser.prototype.onGridKey = function (e) {
    var keys = { ArrowRight: 1, ArrowDown: 'row', ArrowLeft: -1, ArrowUp: '-row', Home: 'first', End: 'last' };
    if (!(e.key in keys)) return;
    var visible = this.cards.filter(function (c) { return !c.hidden; });
    var i = visible.indexOf(document.activeElement);
    if (i < 0) return;
    e.preventDefault();
    var cols = Math.max(1, Math.round(this.grid.clientWidth / visible[0].offsetWidth));
    var k = keys[e.key];
    var j = k === 'first' ? 0 : k === 'last' ? visible.length - 1 : k === 'row' ? i + cols : k === '-row' ? i - cols : i + k;
    j = Math.max(0, Math.min(visible.length - 1, j));
    visible.forEach(function (c) { c.tabIndex = -1; });
    visible[j].tabIndex = 0;
    visible[j].focus();
  };

  // A design may list the shirt colours it can be printed on (design.colours).
  Customiser.prototype.available = function (d, colour) {
    return this.g.colourOk(d, colour || this.colour);
  };

  Customiser.prototype.setColour = function (name) {
    var c = this.g.colours[name];
    if (!c || name === this.colour) return;
    this.colour = name;
    var self = this;
    this.cards.forEach(function (card) {
      var ok = self.available(self.byCode[card.getAttribute('data-code')], name);
      card.classList.toggle('is-unavailable', !ok);
      card.setAttribute('aria-disabled', String(!ok));
    });
    this.filter();
    if (this.design && !this.available(this.design)) {
      var d = this.design;
      this.clearDesign(d.code + " can't be printed on " + name + ', so it has been cleared. Pick another design' +
        (d.colourNote ? ', or go back to a light shirt colour.' : '.'));
    }
    this.root.style.setProperty('--bbt-tee', c.hex);
    this.root.setAttribute('data-bbt-colour', name);

    this.showPhoto();
  };

  // Crossfade to the photo for the current colour and view, once it (and its
  // shading map) has loaded. The print only shows on the back.
  Customiser.prototype.showPhoto = function () {
    var self = this, colour = this.colour, view = this.view;
    var slug = this.g.colours[colour].slug;
    var photoUrl = this.teeUrl(slug, 'photo', view);
    var shadeUrl = this.teeUrl(slug, 'shade', 'back');
    var loaded = 0;
    function swap() {
      if (++loaded < 2 || self.colour !== colour || self.view !== view) return;   // ignore stale picks
      var next = self.photoFront === 'a' ? 'b' : 'a';
      var el = self.svg.querySelector('.bbt-photo-' + next);
      el.setAttribute('href', photoUrl);
      el.classList.add('is-on');
      self.svg.querySelector('.bbt-photo-' + self.photoFront).classList.remove('is-on');
      self.photoFront = next;
      self.svg.querySelectorAll('.bbt-shade').forEach(function (n) { n.setAttribute('href', shadeUrl); });
      if (self.design) setTimeout(function () { self.updateMini(); }, 360);
    }
    [photoUrl, shadeUrl].forEach(function (u) { var im = new Image(); im.onload = im.onerror = swap; im.src = u; });
  };

  Customiser.prototype.setView = function (view) {
    if (view === this.view) return;
    this.view = view;
    this.stage.classList.toggle('is-front', view === 'front');
    this.stage.querySelectorAll('.bbt-views [data-view]').forEach(function (b) {
      b.setAttribute('aria-pressed', String(b.getAttribute('data-view') === view));
    });
    if (view === 'front') this.toggleZoom(false);
    this.zoomBtn.disabled = view === 'front' || !this.design;
    this.showPhoto();
  };

  Customiser.prototype.clearDesign = function (message) {
    this.design = null;
    this.input.value = '';
    this.codeInput.value = '';
    this.chosen.textContent = 'None selected';
    this.cards.forEach(function (c) { c.setAttribute('aria-checked', 'false'); });
    this.updateTabStops();
    this.svg.querySelectorAll('.bbt-print').forEach(function (n) { n.classList.remove('is-on'); });
    this.toggleZoom(false);
    this.zoomBtn.disabled = true;
    this.mini.classList.remove('is-on');
    this.hint.textContent = 'Pick a design to see it on your shirt';
    this.hint.hidden = false;
    this.updateColourLinks();
    if (message) {
      this.error.textContent = message;
      this.error.hidden = false;
    }
    this.root.dispatchEvent(new CustomEvent('bbt:design', { bubbles: true, detail: null }));
  };

  Customiser.prototype.setDesign = function (code, silent) {
    var d = this.byCode[code];
    if (!d) return;
    if (!this.available(d)) {
      this.error.textContent = d.code + " can't be printed on " + this.colour + '.' + (d.colourNote ? ' It is available on light colours.' : '');
      this.error.hidden = false;
      return;
    }
    this.design = d;
    var label = d.code + ' - ' + d.name;
    this.input.value = d.name;
    this.codeInput.value = d.code;
    this.chosen.textContent = label;
    this.error.hidden = true;
    this.picker.classList.remove('bbt-invalid');
    this.cards.forEach(function (c) { c.setAttribute('aria-checked', String(c.getAttribute('data-code') === code)); });
    this.updateTabStops();
    this.hint.hidden = true;
    this.setView('back');
    this.zoomBtn.disabled = false;

    // crossfade between two image slots once the new art has loaded
    var self = this;
    var url = this.assetUrl(d.code, 'full');
    var img = new Image();
    img.onerror = function () {
      if (self.design !== d) return;
      self.hint.textContent = 'Preview not available for ' + d.code;
      self.hint.hidden = false;
    };
    img.onload = function () {
      if (self.design !== d) return;          // a newer pick won the race
      if (!d.aspect && img.naturalWidth) d.aspect = img.naturalWidth / img.naturalHeight;
      var box = printBox(d, self.g, self.colour);
      var next = self.front === 'a' ? 'b' : 'a';
      var el = self.svg.querySelector('.bbt-print-' + next);
      var old = self.svg.querySelector('.bbt-print-' + self.front);
      var art = el.querySelector('.bbt-art');
      var mask = self.svg.querySelector('#' + self.id + '-m' + next + ' .bbt-mask');
      art.setAttribute('href', url); setBox(art, box);
      mask.setAttribute('href', url); setBox(mask, box);
      el.querySelector('.bbt-shade').setAttribute('href', self.teeUrl(self.g.colours[self.colour].slug, 'shade', 'back'));
      el.classList.add('is-on');
      old.classList.remove('is-on');
      self.front = next;
      self.setZoomTarget(box);
      self.updateMini();
    };
    img.src = url;

    this.updateColourLinks();
    if (!silent) this.root.dispatchEvent(new CustomEvent('bbt:design', { bubbles: true, detail: { code: d.code, name: d.name, label: label } }));
  };

  Customiser.prototype.setZoomTarget = function (box) {
    var W = this.g.frameW || 100;
    var cx = box.x + box.w / 2, cy = box.y + box.h / 2;
    var scale = Math.min(2.6, 86 / Math.max(box.w / W * 100, box.h));
    this.stage.style.setProperty('--bbt-zs', scale.toFixed(3));
    this.stage.style.setProperty('--bbt-zx', ((W / 2 - cx) / W * 100).toFixed(2) + '%');
    this.stage.style.setProperty('--bbt-zy', (50 - cy).toFixed(2) + '%');
  };

  Customiser.prototype.toggleZoom = function (force) {
    var on = typeof force === 'boolean' ? force : !this.stage.classList.contains('is-zoomed');
    this.stage.classList.toggle('is-zoomed', on);
    this.zoomBtn.setAttribute('aria-pressed', String(on));
    this.zoomBtn.querySelector('span').textContent = on ? 'Show whole shirt' : 'Zoom in on design';
  };

  // Returns true if a design is chosen; otherwise flags the picker.
  Customiser.prototype.validate = function () {
    if (this.design) return true;
    this.error.textContent = 'Choose a design before adding to cart.';
    this.error.hidden = false;
    this.picker.classList.add('bbt-invalid');
    scrollToEl(this.picker);
    return false;
  };

  // Nova scrolls the page with Lenis; a native smooth scroll fights it and
  // can leave the page stuck mid-way, so hand the scroll to Lenis when it's there.
  function scrollToEl(el) {
    if (window.lenis && typeof window.lenis.scrollTo === 'function') {
      var r = el.getBoundingClientRect();
      window.lenis.scrollTo(el, { offset: -Math.max(0, (window.innerHeight - r.height) / 2) });
    } else {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  // Small static preview (cart thumbnails etc.)
  Customiser.prototype.snapshot = function () {
    var clone = this.cloneSVG();
    var wrap = document.createElement('div');
    wrap.className = 'bbt-snapshot';
    wrap.style.aspectRatio = (this.g.frameW || 100) + ' / 100';
    wrap.setAttribute('style', this.root.getAttribute('style') || '');
    wrap.appendChild(clone);
    return wrap;
  };

  /* ---------- Shopify adapter ---------- */
  // One product per blank colour. The Liquid snippet renders:
  //   <div data-bbt-root data-garment="tee" data-colour="Black"
  //        data-asset-base="https://cdn.shopify.com/s/files/.../files/">
  //     <script type="application/json" data-bbt-designs>[...]</script>
  //     <script type="application/json" data-bbt-colour-links>{"White": "/products/...", ...}</script>
  //     <div data-bbt-stage></div><div data-bbt-colours></div><div data-bbt-picker></div>
  //   </div>
  // Assets live flat in Shopify Files: bbt-<code>[-v<N>][-thumb].webp for designs and
  // bbt-<folder>-<slug>[-back][-shade].webp for the garment photos.
  function autoInit() {
    document.querySelectorAll('[data-bbt-root]:not([data-bbt-ready])').forEach(function (root) {
      root.setAttribute('data-bbt-ready', '');
      var json = function (sel) { var n = root.querySelector(sel); return n ? JSON.parse(n.textContent) : null; };
      var base = root.getAttribute('data-asset-base') || '';
      var garment = root.getAttribute('data-garment') || 'tee';
      var folder = GARMENTS[garment].folder;
      var findForm = function () {
        return document.getElementById(root.getAttribute('data-form-id') || '') ||
          document.querySelector('form[data-product-form]') ||
          document.querySelector('main form[action$="/cart/add"]') ||
          document.querySelector('form[action*="/cart/add"]');
      };
      var form = findForm();
      if (form && !form.id) form.id = 'bbt-product-form';

      var designs = json('[data-bbt-designs]') || [];
      // replaced artwork lives under a versioned file name (Shopify Files can't overwrite)
      var ver = {};
      designs.forEach(function (d) { if (d.v) ver[d.code] = d.v; });
      var c = new Customiser(root, {
        garment: garment,
        designs: designs,
        colour: root.getAttribute('data-colour'),
        colourLinks: json('[data-bbt-colour-links]'),
        design: designFromUrl(),
        formId: form ? form.id : null,
        assetUrl: function (code, kind) { return base + 'bbt-' + code + (ver[code] ? '-v' + ver[code] : '') + (kind === 'thumb' ? '-thumb' : '') + '.webp'; },
        teeUrl: function (slug, kind, view) {
          return base + 'bbt-' + folder + '-' + slug + (view === 'back' ? '-back' : '') + (kind === 'shade' ? '-shade' : '') + '.webp';
        }
      });
      root.bbt = c;
      if (root.hasAttribute('data-move-stage')) moveStage(root, c);
      if (!form) return;
      wireForm(root, c, findForm);
    });
  }

  // The chosen design rides to the cart as line item properties held in hidden
  // inputs INSIDE the product form, with the value attribute kept in step:
  //  - Nova rebuilds the whole buy-buttons block (form included) on every size
  //    change, so the inputs are re-added whenever that happens;
  //  - Essential Cart Drawer's sticky add-to-cart bar copies properties from
  //    inputs inside the main product form, reading getAttribute('value').
  // Every add-to-cart path (Nova's button, the app's sticky bar) is checked at
  // window level, so a rebuilt form can't drop the "choose a design" guard.
  var PROPS = [['properties[Design Art ID]', 'code'], ['properties[Design]', 'name']];

  function wireForm(root, c, findForm) {
    // the picker's own inputs would double up in the form data; the form copies replace them
    [c.input, c.codeInput].forEach(function (el) { el.removeAttribute('name'); el.removeAttribute('form'); });

    function sync() {
      var form = findForm();
      if (!form) return null;
      PROPS.forEach(function (p) {
        var el = form.querySelector('input[data-bbt-prop="' + p[1] + '"]');
        if (!el) {
          el = document.createElement('input');
          el.type = 'hidden';
          el.name = p[0];
          el.setAttribute('data-bbt-prop', p[1]);
          form.appendChild(el);
        }
        var v = c.design ? c.design[p[1]] : '';
        el.value = v;
        el.setAttribute('value', v);
        el.disabled = !v;      // an empty property is left out rather than sent blank
      });
      return form;
    }
    sync();
    root.addEventListener('bbt:design', sync);

    // re-add the inputs after Nova swaps the buy-buttons block
    var region = (findForm() || root).closest('x-product-info') || document.body;
    var queued = false;
    new MutationObserver(function () {
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () {
        queued = false;
        var form = findForm();
        if (form && (!form.querySelector('input[data-bbt-prop]') ||
            form.querySelector('input[data-bbt-prop="code"]').value !== (c.design ? c.design.code : ''))) sync();
      });
    }).observe(region, { childList: true, subtree: true });

    function isAdd(e) {
      var path = e.composedPath ? e.composedPath() : [e.target];
      for (var i = 0; i < path.length; i++) {
        var n = path[i];
        if (!n || n.nodeType !== 1) continue;
        if (n.matches('[data-essential-cart-element="add-to-cart-button"]')) return true;
        if (n.matches('button[type="submit"], input[type="submit"], button[name="add"]')) {
          var f = n.form || n.closest('form');
          return !!f && f === findForm();
        }
      }
      return false;
    }
    function guard(e) {
      sync();
      if (c.validate()) return;
      e.preventDefault();
      e.stopImmediatePropagation();
    }
    window.addEventListener('click', function (e) { if (isAdd(e)) guard(e); }, true);
    window.addEventListener('submit', function (e) { if (e.target === findForm()) guard(e); }, true);
  }

  // Nova: show the live preview in the gallery column instead of the
  // product photos (those still serve collection cards and search).
  function moveStage(root, c) {
    var layout = root.closest('.aside-content');
    var aside = layout && layout.querySelector('.aside-content__aside');
    if (!aside) return;
    var gallery = aside.querySelector('x-product-gallery');
    if (gallery) gallery.style.display = 'none';
    var wrap = document.createElement('div');
    wrap.className = 'bbt-gallery';
    wrap.appendChild(c.stage);
    var note = root.querySelector('[data-bbt-stage-note]');
    if (note) wrap.appendChild(note);
    aside.insertBefore(wrap, aside.firstChild);
  }

  // ?design=BB27 carries the customer's pick across when they switch colour
  function designFromUrl() {
    var m = /[?&]design=([A-Za-z]{2}\d+)/.exec(location.search);
    return m ? m[1].toUpperCase() : null;
  }

  window.BBTee = { Customiser: Customiser, GARMENTS: GARMENTS, COLOURS: GARMENTS.tee.colours, autoInit: autoInit, printBox: printBox, designFromUrl: designFromUrl };
  if (document.querySelector('[data-bbt-root]')) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', autoInit);
    else autoInit();
  }
})();
